import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { DatePicker, Flex, Input, InputNumber, Select, Tag } from 'antd';

import classnames from 'classnames';
import dayjs from 'dayjs';
import { isEmail } from 'validator';

import { capitalize } from '../../../helpers/common';
import { getTaskNullState, validateTask } from '../../../helpers/task';

import { DATE_FORMAT } from '../../../constants/common';
import { CUSTOM_RECURRING_UNITS, MINUTE_STEP, RECURRING_TYPES, RELATION_TYPES } from '../../../constants/event';
import messages from '../../../constants/messages';
import { TASK_MODAL_MODE } from '../../../constants/task';
import { TASK_PRIORITIES, TASK_STATUSES } from '../../../constants/task';

import { useModal } from '../../../hooks/useModal';

import {
  acceptTask,
  createNewTask,
  declineTask,
  deleteTask,
  getAllTasks,
  getTaskById,
  sendRelationQuery,
  updateTask
} from '../../../redux/actions/task';

import AppModal from '../../../components/AppModal';

import styles from '../styles.module.scss';

import ConfirmationModal from '../../Modals/ConfirmationModal';
import { errorToastHandler } from '../../action_notifier';
import TaskDetails from './TaskDetails';
import TaskModalFooter from './TaskModalFooter';

const TaskModal = ({ isOpen, mode, onCancel, onSubmit }) => {
  const TASK_NULL_STATE = getTaskNullState();
  const dispatch = useDispatch();

  const taskState = useSelector(state => state.task);
  const userState = useSelector(state => state.user);

  const [confirmationModalVisible, openConfirmationModal, closeConfirmationModal] = useModal();
  const [deleteRecurringTasksModalVisible, openDeleteRecurringTasksModal, closeDeleteRecurringTasksModal] = useModal();

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [externalEmails, setExternalEmails] = useState([]);
  const [externalEmailInputValue, setExternalEmailInputValue] = useState('');
  const [modalMode, setModalMode] = useState(mode);
  const [newTask, setNewTask] = useState(TASK_NULL_STATE);
  const [relationOptions, setRelationOptions] = useState([]);

  const defaultUserId = useMemo(
    () => userState?.assignableUsers?.find(user => user.email === userState.user?.email)?.id,
    [userState]
  );

  useEffect(() => {
    if (!userState?.assignableUsers?.length) return;

    setAssignedUsers([defaultUserId]);
  }, [userState]);

  useEffect(() => {
    if (mode !== TASK_MODAL_MODE.CREATE && taskState.currentTask.id && isOpen) {
      dispatch(getTaskById(taskState.currentTask.id));
    }
  }, [isOpen]);

  const getRelationOptions = relationType => {
    dispatch(sendRelationQuery(relationType)).then(data => {
      setRelationOptions(Array.isArray(data) ? data : data.records);
    });
  };

  const onCancelClick = () => {
    closeConfirmationModal();
    setNewTask(TASK_NULL_STATE);

    if (taskState?.currentTask?.is_owner) {
      setModalMode(mode);
    }

    setAssignedUsers([defaultUserId]);
    onCancel();
  };

  useEffect(() => {
    setNewTask(getTaskNullState());
  }, [isOpen]);

  useEffect(() => {
    if (newTask.relationType) {
      getRelationOptions(newTask.relationType);
    }
  }, [newTask.relationType]);

  const onChange = (value, field) => {
    const formattedValue = value ?? TASK_NULL_STATE[field];

    if (field === 'relationType') {
      setNewTask(prevState => ({
        ...prevState,
        relationType: formattedValue,
        relations:
          value === taskState?.currentTask?.relationType
            ? taskState?.currentTask?.relations?.map(relation => ({
                value: relation.id,
                label: `${relation.identifier} ${relation.name}`
              }))
            : []
      }));

      return;
    }

    setNewTask(prevState => ({ ...prevState, [field]: formattedValue }));
  };

  const onChangeRelation = (values, selectedOptions) => {
    setNewTask(prevState => ({ ...prevState, relations: selectedOptions }));
  };

  const onCreateClick = () => {
    const payload = {
      ...newTask,
      relationIds: newTask.relations?.map(relation => relation.value),
      userIds: assignedUsers,
      externalEmails
    };

    const error = validateTask(payload);

    if (error) {
      errorToastHandler(error);
    } else {
      dispatch(createNewTask(payload)).then(res => {
        if (res) {
          dispatch(getAllTasks(taskState.queryParams));
          onSubmit?.();
        }
      });
    }
  };

  const onDeleteSubmit = () => {
    dispatch(deleteTask(taskState.currentTask.id)).then(() => {
      dispatch(getAllTasks(taskState.queryParams));
      closeConfirmationModal();
      setNewTask(TASK_NULL_STATE);
      onCancel();
    });
  };

  const onDeleteRecurringTasksSubmit = () => {
    dispatch(deleteTask(taskState.currentTask.id, { count: 'future' })).then(() => {
      dispatch(getAllTasks(taskState.queryParams));
      closeDeleteRecurringTasksModal();
      setNewTask(TASK_NULL_STATE);
      onCancel();
    });
  };

  const onEditClick = () => {
    const task = {
      ...taskState.currentTask,
      dueDate: dayjs(taskState.currentTask.due_date).format(),
      remindDate: taskState.currentTask.remind_at ? dayjs(taskState.currentTask.remind_at).format() : null
    };

    setNewTask({
      ...task,
      relations: task?.relations?.map(relation => ({
        value: relation.id,
        label: `${relation.identifier} ${relation.name}`
      }))
    });
    setAssignedUsers(task?.userIds);
    setExternalEmails(task?.external_emails);
    setModalMode(TASK_MODAL_MODE.UPDATE);
  };

  const onUpdateClick = () => {
    const formattedTask = {
      description: newTask.description,
      dueDate: dayjs(newTask.dueDate).format(),
      priority: newTask.priority,
      recurringEndDate: newTask.recurringEndDate || undefined,
      recurringNumber: newTask.recurringNumber || undefined,
      recurringType: newTask.recurringType || undefined,
      recurringUnit: newTask.recurringUnit || undefined,
      relationIds: newTask.relations?.map(relation => relation.value),
      relationType: newTask.relationType || null,
      remindDate: newTask.remindDate ? dayjs(newTask.remindDate).format() : null,
      status: newTask.status,
      title: newTask.title,
      userIds: [...assignedUsers],
      externalEmails
    };

    const error = validateTask(formattedTask);

    if (error) {
      errorToastHandler(error);
    } else {
      dispatch(updateTask(newTask.id, formattedTask)).then(res => {
        if (res) {
          dispatch(getAllTasks(taskState.queryParams));
          setNewTask(TASK_NULL_STATE);
          setAssignedUsers([]);
          setModalMode(TASK_MODAL_MODE.VIEW);
          onCancel();
        }
      });
    }
  };

  const onDeclineClick = () => {
    dispatch(declineTask(taskState.currentTask.id)).then(res => {
      if (res) {
        dispatch(getAllTasks(taskState.queryParams));
        setNewTask(TASK_NULL_STATE);
        setModalMode(TASK_MODAL_MODE.VIEW);
        onCancel();
      }
    });
  };

  const onAcceptClick = () => {
    dispatch(acceptTask(taskState.currentTask.id)).then(res => {
      if (res) {
        dispatch(getAllTasks(taskState.queryParams));
        setNewTask(TASK_NULL_STATE);
        setModalMode(TASK_MODAL_MODE.VIEW);
        onCancel();
      }
    });
  };

  function setUsers(ids) {
    setAssignedUsers(ids);
  }

  const handleExternalEmailInputConfirm = () => {
    if (isEmail(externalEmailInputValue) && externalEmails.indexOf(externalEmailInputValue) === -1) {
      setExternalEmails([...externalEmails, externalEmailInputValue]);
    }

    setExternalEmailInputValue('');
  };

  const handleExternalEmailInputChange = e => {
    setExternalEmailInputValue(e.target.value);
  };

  const handleRemoveExternalEmail = email => {
    const newExternalEmails = externalEmails.filter(externalEmail => externalEmail !== email);

    setExternalEmails(newExternalEmails);
  };

  return (
    <div className="task-modal-component">
      <ConfirmationModal
        title="Delete task"
        description={messages.ARCHIVE_CONFIRMATION('task')}
        isOpen={confirmationModalVisible}
        onCancel={closeConfirmationModal}
        onSubmit={onDeleteSubmit}
      />

      <ConfirmationModal
        description="This will permanently delete this task and all its future recurring instances. This action cannot be undone."
        title="Delete all recurring tasks"
        isOpen={deleteRecurringTasksModalVisible}
        onCancel={closeDeleteRecurringTasksModal}
        onSubmit={onDeleteRecurringTasksSubmit}
      />

      <AppModal
        title={`${modalMode} task`}
        isOpen={isOpen}
        footer={
          <TaskModalFooter
            currentTask={taskState?.currentTask}
            modalMode={modalMode}
            newTask={newTask}
            onAcceptClick={onAcceptClick}
            onCancelClick={onCancelClick}
            onCreateClick={onCreateClick}
            onDeclineClick={onDeclineClick}
            onDeleteClick={openConfirmationModal}
            onDeleteRecurringTasksClick={openDeleteRecurringTasksModal}
            onEditClick={onEditClick}
            onUpdateClick={onUpdateClick}
          />
        }
        handleCancel={onCancelClick}>
        <>
          {modalMode === TASK_MODAL_MODE.CREATE || modalMode === TASK_MODAL_MODE.UPDATE ? (
            <Flex className="py-4" vertical={true} gap={10}>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="title">
                  Title
                </label>
                <Input id="title" value={newTask.title} onChange={e => onChange(e.target.value, 'title')} />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="description">
                  Description
                </label>
                <Input
                  id="description"
                  value={newTask.description}
                  onChange={e => onChange(e.target.value, 'description')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="status">
                  Status
                </label>
                <Select
                  id="status"
                  className="w-full"
                  value={newTask.status}
                  options={TASK_STATUSES}
                  onChange={value => onChange(value, 'status')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="priority">
                  Priority
                </label>
                <Select
                  id="priority"
                  className="w-full"
                  value={newTask.priority}
                  options={TASK_PRIORITIES}
                  onChange={value => onChange(value, 'priority')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={classnames(styles.label, 'required-field')} htmlFor="due-date">
                  Due date
                </label>
                <DatePicker
                  id="due-date"
                  className="w-full"
                  variant="outlined"
                  allowClear={false}
                  defaultValue={dayjs(newTask.dueDate)}
                  disabledDate={current => current && current < dayjs().startOf('day')}
                  format={DATE_FORMAT.DATETIME}
                  showTime={{ minuteStep: MINUTE_STEP }}
                  onChange={e => onChange(e, 'dueDate')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="remind-date">
                  Remind date
                </label>
                <DatePicker
                  id="remind-date"
                  className="w-full"
                  variant="outlined"
                  allowClear={false}
                  defaultValue={newTask.remindDate ? dayjs(newTask.remindDate) : undefined}
                  disabledDate={current => current && current > dayjs(newTask.dueDate).startOf('day')}
                  format={DATE_FORMAT.DATETIME}
                  showTime={{ minuteStep: MINUTE_STEP }}
                  onChange={e => onChange(e, 'remindDate')}
                />
              </Flex>
              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="relation">
                  Relation
                </label>
                <Select
                  id="relation"
                  className="w-full"
                  allowClear={true}
                  options={RELATION_TYPES.map(type => ({ value: type, label: capitalize(type) }))}
                  value={{ value: newTask.relationType, label: capitalize(newTask.relationType) }}
                  onChange={value => onChange(value, 'relationType')}
                />
              </Flex>

              {newTask.relationType && (
                <Flex vertical={true} gap={5}>
                  <label className={classnames(styles.label, 'text-capitalize')} htmlFor="relation-type">
                    {newTask.relationType}
                  </label>
                  <Select
                    id="relation-type"
                    className="w-full"
                    mode="multiple"
                    optionFilterProp="label"
                    allowClear={true}
                    options={relationOptions?.map(option => ({
                      value: option.id,
                      label: `${option.identifier} ${option.name}`
                    }))}
                    showSearch={true}
                    value={newTask.relations}
                    onChange={onChangeRelation}
                  />
                </Flex>
              )}

              <Flex vertical={true} gap={5}>
                <label className={styles.label}>Recurring type</label>
                <Select
                  className="w-full"
                  allowClear={true}
                  value={{ label: capitalize(newTask.recurringType) }}
                  options={RECURRING_TYPES.map(type => ({ value: type, label: capitalize(type) }))}
                  onChange={value => onChange(value, 'recurringType')}
                />
              </Flex>

              {newTask.recurringType && (
                <>
                  <Flex vertical={true} gap={5}>
                    <label className={classnames(styles.label, 'required-field')} htmlFor="recurring-end-date">
                      Recurring end date
                    </label>
                    <DatePicker
                      id="recurring-end-date"
                      className="w-full"
                      variant="outlined"
                      allowClear={false}
                      defaultValue={newTask.recurringEndDate ? dayjs(newTask.recurringEndDate) : undefined}
                      format={DATE_FORMAT.DATETIME}
                      showTime={{ minuteStep: MINUTE_STEP }}
                      onChange={e => onChange(e, 'recurringEndDate')}
                    />
                  </Flex>
                  {newTask.recurringType === 'custom' && (
                    <Flex vertical={true} gap={5}>
                      <label className={classnames(styles.label, 'required-field')}>Custom Recurrence</label>
                      <Flex className="w-full" gap="small">
                        <InputNumber min={1} className="w-full" onChange={e => onChange(e, 'recurringNumber')} />
                        <Select
                          className="w-full"
                          onChange={value => onChange(value, 'recurringUnit')}
                          options={CUSTOM_RECURRING_UNITS.map(unit => ({
                            value: unit,
                            label: capitalize(unit)
                          }))}
                        />
                      </Flex>
                    </Flex>
                  )}
                </>
              )}

              <Flex vertical={true} gap={5}>
                <label className={styles.label} htmlFor="users">
                  Users
                </label>
                <Flex className="w-full" vertical={true}>
                  <Select
                    className="w-full"
                    mode="multiple"
                    id="users"
                    optionFilterProp="label"
                    allowClear={true}
                    value={assignedUsers}
                    options={userState.assignableUsers?.map(user => ({
                      value: user.id,
                      label: user.email,
                      disabled: user.id === defaultUserId
                    }))}
                    showSearch={true}
                    onChange={setUsers}
                  />
                  <Link to="/profile?tab=linked-users">Can&apos;t find the user? Link an account.</Link>
                </Flex>
              </Flex>
              <Flex vertical gap={5}>
                <label className={styles.label} htmlFor="users">
                  Invite emails
                </label>
                <Flex className="w-full" vertical={true}>
                  <Input
                    className="mb-2"
                    value={externalEmailInputValue}
                    onChange={handleExternalEmailInputChange}
                    onBlur={handleExternalEmailInputConfirm}
                    onPressEnter={handleExternalEmailInputConfirm}
                  />
                  <div>
                    {externalEmails.map(email => (
                      <span key={email} style={{ display: 'inline-block' }}>
                        <Tag
                          closable
                          onClose={e => {
                            e.preventDefault();
                            handleRemoveExternalEmail(email);
                          }}>
                          {email}
                        </Tag>
                      </span>
                    ))}
                  </div>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            <TaskDetails task={taskState?.currentTask} />
          )}
        </>
      </AppModal>
    </div>
  );
};

export default memo(TaskModal);
